import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CustomInput from 'components/CustomInput/CustomInput.js';
import Button from 'components/CustomButtons/Button.js';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import config from '../../config/config.json';
import { useHeaderLinks } from 'components/Header/HeaderLinksContext';
import LoginPrompt from 'components/LoginPrompt/LoginPrompt';
import { MenuItem, SelectChangeEvent } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { GB, DE, KR, CN } from 'country-flag-icons/react/3x2';
import { availableLanguages } from '../../i18n';
import d2ricon from 'assets/img/d2r-logo.png';
import d2rsicon from 'assets/img/d2rsiconblock.png';
import poe2icon from 'assets/img/poe2-logo.webp';
import { CustomSelect } from 'components/CustomInput/CustomSelect';

const useStyles = makeStyles<Theme>((theme) => ({
  cardHeader: {
    textAlign: 'center',
    '& h4': {
      marginTop: '0',
      marginBottom: '0',
      fontSize: '1.5rem',
    },
  },
  cardBody: {
    padding: '20px',
  },
  textCenter: {
    textAlign: 'center',
  },
  textArea: {
    marginRight: '15px',
    marginLeft: '15px',
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
  },
  flagWrapper: {
    width: '24px',
    height: '18px',
    marginRight: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
  gameIcon: {
    width: '24px',
    height: '24px',
    marginRight: theme.spacing(1),
    verticalAlign: 'middle',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
  },
  submitButton: {
    marginTop: theme.spacing(3),
  },
}));

type ApplicationType = 'graphic' | 'community' | 'tools';
type GameType = 'd2r' | 'poe2' | 'other' | 'd2rs';
type CommunityType = 'mixed' | 'pvp' | 'pvm';
type ApplicationTypeTools = 'sell' | 'integrate' | 'help' | 'contribute';

interface FormData {
  type: ApplicationType;
  name: string;
  message: string;
  // Graphic Designer specific
  portfolio?: string;
  // Community specific
  game?: GameType;
  communityName?: string;
  communityLink?: string;
  communityType?: CommunityType;
  communityLanguage?: string;
  // Tools/Devs specific
  applicationType?: ApplicationTypeTools;
  website?: string;
}

const flagComponents = {
  en: GB,
  de: DE,
  ko: KR,
  zh: CN,
};

export default function WorkWithUsForm() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isLoggedIn } = useHeaderLinks();
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = React.useState(false);
  const [formData, setFormData] = React.useState<FormData>({
    type: (searchParams.get('t') as ApplicationType) || 'graphic',
    name: '',
    message: '',
  });

  const handleInputChange =
    (field: keyof FormData) =>
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }> | SelectChangeEvent) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const getFlagComponent = (languageCode: string) => {
    const FlagComponent = flagComponents[languageCode as keyof typeof flagComponents];
    return FlagComponent ? (
      <span className={classes.flagWrapper}>
        <FlagComponent />
      </span>
    ) : null;
  };

  const getGameIcon = (game: GameType) => {
    switch (game) {
      case 'd2r':
        return <img src={d2ricon} alt="D2R" className={classes.gameIcon} />;
      case 'd2rs':
        return <img src={d2rsicon} alt="D2RS" className={classes.gameIcon} />;
      case 'poe2':
        return <img src={poe2icon} alt="POE2" className={classes.gameIcon} />;
      default:
        return null;
    }
  };

  const sendContactForm = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');

      // Format all form data into a readable message
      let formattedMessage = '';

      switch (formData.type) {
        case 'graphic':
          formattedMessage = `Type: Graphic Designer\nName: ${formData.name}\nPortfolio: ${formData.portfolio}\nMessage: ${formData.message}`;
          break;
        case 'community':
          formattedMessage = `Type: Community\nName: ${formData.name}\nGame: ${formData.game}\nCommunity Name: ${formData.communityName}\nCommunity Link: ${formData.communityLink}\nCommunity Type: ${formData.communityType}\nLanguage: ${formData.communityLanguage}\nMessage: ${formData.message}`;
          break;
        case 'tools':
          formattedMessage = `Type: Tools/Devs\nName: ${formData.name}\nGame: ${formData.game}\nApplication Type: ${formData.applicationType}\nWebsite: ${formData.website}\nMessage: ${formData.message}`;
          break;
      }

      await axios.post(
        config.backend_base_url + 'd2rs/contact/add',
        { message: formattedMessage },
        {
          headers: { 'session-token': sessionToken },
        }
      );
      toast.success(t('workWithUs.success'));
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 20000);
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('workWithUs.error'));
    }
  };

  if (!isLoggedIn) {
    return <LoginPrompt message={t('workWithUs.loginRequired')} />;
  }

  const renderGraphicDesignerForm = () => (
    <>
      <GridItem xs={12} sm={12} md={6}>
        <CustomInput
          labelText={t('workWithUs.form.name')}
          id="name"
          value={formData.name}
          formControlProps={{
            fullWidth: true,
          }}
          onChange={handleInputChange('name')}
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <CustomInput
          labelText={t('workWithUs.form.portfolio')}
          id="portfolio"
          value={formData.portfolio}
          formControlProps={{
            fullWidth: true,
          }}
          onChange={handleInputChange('portfolio')}
        />
      </GridItem>
    </>
  );

  const renderCommunityForm = () => (
    <>
      <GridItem xs={12} sm={12} md={6}>
        <CustomSelect
          labelText={t('workWithUs.form.game')}
          id="community-game"
          value={formData.game || ''}
          onChange={handleInputChange('game')}
          formControlProps={{ fullWidth: true }}
        >
          <MenuItem value="d2r" className={classes.menuItem}>
            {getGameIcon('d2r')}D2R
          </MenuItem>
          <MenuItem value="poe2" className={classes.menuItem}>
            {getGameIcon('poe2')}POE2
          </MenuItem>
          <MenuItem value="other">{t('workWithUs.form.other')}</MenuItem>
        </CustomSelect>
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <CustomSelect
          labelText={t('workWithUs.form.communityLanguage')}
          id="community-language"
          value={formData.communityLanguage || ''}
          onChange={handleInputChange('communityLanguage')}
          formControlProps={{ fullWidth: true }}
        >
          {availableLanguages.map((language) => (
            <MenuItem key={language.code} value={language.code} className={classes.menuItem}>
              {getFlagComponent(language.code)}
              {language.name}
            </MenuItem>
          ))}
        </CustomSelect>
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <CustomInput
          labelText={t('workWithUs.form.name')}
          id="name"
          value={formData.name}
          formControlProps={{
            fullWidth: true,
          }}
          onChange={handleInputChange('name')}
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <CustomInput
          labelText={t('workWithUs.form.communityName')}
          id="communityName"
          value={formData.communityName}
          formControlProps={{
            fullWidth: true,
          }}
          onChange={handleInputChange('communityName')}
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <CustomInput
          labelText={t('workWithUs.form.communityLink')}
          id="communityLink"
          value={formData.communityLink}
          formControlProps={{
            fullWidth: true,
          }}
          onChange={handleInputChange('communityLink')}
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <CustomSelect
          labelText={t('workWithUs.form.communityType')}
          id="community-type"
          value={formData.communityType || ''}
          onChange={handleInputChange('communityType')}
          formControlProps={{ fullWidth: true }}
        >
          <MenuItem value="mixed">{t('workWithUs.form.mixed')}</MenuItem>
          <MenuItem value="pvp">PVP</MenuItem>
          <MenuItem value="pvm">PVM</MenuItem>
        </CustomSelect>
      </GridItem>
    </>
  );

  const renderToolsForm = () => (
    <>
      <GridItem xs={12} sm={12} md={6}>
        <CustomSelect
          labelText={t('workWithUs.form.game')}
          id="tools-game"
          value={formData.game || ''}
          onChange={handleInputChange('game')}
          formControlProps={{ fullWidth: true }}
        >
          <MenuItem value="d2rs" className={classes.menuItem}>
            {getGameIcon('d2rs')}D2RS
          </MenuItem>
          <MenuItem value="d2r" className={classes.menuItem}>
            {getGameIcon('d2r')}D2R
          </MenuItem>
          <MenuItem value="poe2" className={classes.menuItem}>
            {getGameIcon('poe2')}POE2
          </MenuItem>
          <MenuItem value="other">{t('workWithUs.form.other')}</MenuItem>
        </CustomSelect>
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <CustomSelect
          labelText={t('workWithUs.form.applicationType')}
          id="tools-application-type"
          value={formData.applicationType || ''}
          onChange={handleInputChange('applicationType')}
          formControlProps={{ fullWidth: true }}
        >
          <MenuItem value="integrate">{t('workWithUs.form.integrateTool')}</MenuItem>
          <MenuItem value="help">{t('workWithUs.form.needHelp')}</MenuItem>
          <MenuItem value="contribute">{t('workWithUs.form.contribute')}</MenuItem>
        </CustomSelect>
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <CustomInput
          labelText={t('workWithUs.form.name')}
          id="name"
          value={formData.name}
          formControlProps={{
            fullWidth: true,
          }}
          onChange={handleInputChange('name')}
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <CustomInput
          labelText={t('workWithUs.form.website')}
          id="website"
          value={formData.website}
          formControlProps={{
            fullWidth: true,
          }}
          onChange={handleInputChange('website')}
        />
      </GridItem>
    </>
  );

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardBody className={classes.cardBody}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={12}>
                <CustomSelect
                  labelText={t('workWithUs.form.applicationType')}
                  id="main-application-type"
                  value={formData.type}
                  onChange={handleInputChange('type')}
                  formControlProps={{ fullWidth: true }}
                >
                  <MenuItem value="graphic">{t('workWithUs.form.graphicDesigner')}</MenuItem>
                  <MenuItem value="tools">{t('workWithUs.form.toolsDevs')}</MenuItem>
                  <MenuItem value="community">{t('workWithUs.form.community')}</MenuItem>
                </CustomSelect>
              </GridItem>

              {formData.type === 'graphic' && renderGraphicDesignerForm()}
              {formData.type === 'community' && renderCommunityForm()}
              {formData.type === 'tools' && renderToolsForm()}

              {formData.type && (
                <GridItem xs={12}>
                  <CustomInput
                    labelText={t('workWithUs.form.message')}
                    id="message"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      multiline: true,
                      rows: 5,
                    }}
                    value={formData.message}
                    onChange={handleInputChange('message')}
                  />
                </GridItem>
              )}
              <GridItem xs={12} className={classes.textCenter}>
                <Button
                  color="primary"
                  onClick={sendContactForm}
                  disabled={submitted}
                  className={classes.submitButton}
                >
                  {t('workWithUs.form.sendMessage')}
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
